import { tabletSidebar } from "./index-tablet";
import { mobileSidebar } from "./index-mobile";
import { STATE_CLOSE, STATE_OPEN, STORAGE_KEY } from "../shared/constant";
import { DesktopSidebarOptions, DeviceType, Orientation, SidebarController, SidebarElements, SidebarFallbackOptions, SidebarState } from "../types/index-type";


const selectSidebarElements = (): SidebarElements => {
  return {
    grid: document.querySelector<HTMLElement>(".grid"),
    sidebar: document.querySelector<HTMLElement>(".sidebar"),
    main: document.querySelector<HTMLElement>(".main"),
    handle: document.querySelector<HTMLElement>(".opcl-button"),
  };
};

// Ensure a persisted sidebar state exists and return it.
const ensureSidebarState = (
  defaultState: SidebarState = STATE_OPEN,
  storageKey: string = STORAGE_KEY
): SidebarState => {
  const stored = localStorage.getItem(storageKey);
  if (stored === STATE_OPEN || stored === STATE_CLOSE) {
    return stored;
  }

  localStorage.setItem(storageKey, defaultState);
  return defaultState;
};

// Apply positional styles for the sidebar toggle based on the requested state.
const applyDesktopSidebarState = (
  state: SidebarState,
  options: DesktopSidebarOptions = {}
): void => {
  const { overlay = false, storageKey = STORAGE_KEY, animate = true } = options;
  const { grid, sidebar, main, handle } = selectSidebarElements();

  if (!grid || !sidebar || !main || !handle) {
    return;
  }

  const transitionDuration = animate ? "0.5s" : "0s";
  grid.style.transition = transitionDuration;
  sidebar.style.transition = transitionDuration;
  main.style.transition = transitionDuration;
  handle.style.transition = transitionDuration;

  const isOpen = state === STATE_OPEN;

  if (overlay) {
    grid.style.left = "0";
    sidebar.style.left = isOpen ? "0" : "-26rem";
    main.style.left = "0";
    main.style.width = "100%";
    handle.style.left = isOpen ? "calc(26rem - 6px)" : "-6px";
  } else {
    grid.style.left = isOpen ? "0" : "-30rem";
    sidebar.style.left = isOpen ? "0" : "-26rem";
    main.style.left = isOpen ? "0" : "4rem";
    main.style.width = isOpen ? "100%" : "auto";
    handle.style.left = isOpen ? "calc(26rem - 6px)" : "-6px";
  }

  handle.textContent = isOpen ? "CLOSE" : "OPEN";

  localStorage.setItem(storageKey, isOpen ? STATE_OPEN : STATE_CLOSE);

  if (!animate) {
    requestAnimationFrame(() => {
      const animatedDuration = "0.5s";
      grid.style.transition = animatedDuration;
      sidebar.style.transition = animatedDuration;
      main.style.transition = animatedDuration;
      handle.style.transition = animatedDuration;
    });
  }
};

// Wire up desktop-style sidebar controls; optional overlay mode keeps main content fixed.
const setupDesktopSidebarFallback = (
  options: SidebarFallbackOptions = {}
): SidebarController | null => {
  const {
    lockOnNarrow = true,
    defaultState = STATE_OPEN,
    overlay = false,
    storageKey = STORAGE_KEY
  } = options;
  const handle = document.querySelector<HTMLElement>(".opcl-button");

  if (!handle || handle.dataset.sidebarFallbackBound === "true") {
    return null;
  }

  const applyStoredState = (animate = true): void => {
    const targetState = ensureSidebarState(defaultState, storageKey);
    applyDesktopSidebarState(targetState, { overlay, storageKey, animate });
  };

  applyStoredState(false);

  handle.addEventListener("click", () => {
    const nextState =
      localStorage.getItem(storageKey) === STATE_OPEN ? STATE_CLOSE : STATE_OPEN;
    applyDesktopSidebarState(nextState, { overlay, storageKey });
  });

  handle.dataset.sidebarFallbackBound = "true";

  let hasInitialisedViewportSync = false;

  const syncForViewport = (): void => {
    const width = window.innerWidth;
    const shouldAnimate = hasInitialisedViewportSync;

    if (width < 768) {
      if (lockOnNarrow) {
        applyDesktopSidebarState(STATE_CLOSE, {
          overlay,
          storageKey,
          animate: shouldAnimate
        });
      } else {
        applyStoredState(shouldAnimate);
      }
    } else {
      if (lockOnNarrow) {
        applyDesktopSidebarState(STATE_OPEN, {
          overlay,
          storageKey,
          animate: shouldAnimate
        });
        localStorage.setItem(storageKey, STATE_OPEN);
      } else {
        applyStoredState(shouldAnimate);
      }
    }

    hasInitialisedViewportSync = true;
  };

  window.addEventListener("load", syncForViewport);
  window.addEventListener("resize", syncForViewport);
  syncForViewport();

  return {
    setState: (state: SidebarState) => applyDesktopSidebarState(state, { overlay, storageKey })
  };
};

// Derive the current orientation for devices that expose window.orientation.
function getDeviceOrientation(
  deviceType: DeviceType
): { deviceType: DeviceType; orientation: Orientation } {
  const orientation: Orientation = (() => {
    if (typeof window.orientation === "number") {
      return Math.abs(window.orientation) === 90 ? "landscape" : "portrait";
    }

    if (window.matchMedia) {
      return window.matchMedia("(orientation: landscape)").matches
        ? "landscape"
        : "portrait";
    }

    return "portrait";
  })();
  // このタイミングでorientationAngleをsetしないと各デバイスごとの取得特にmobileで強制的にlandscapeになる
  localStorage.setItem("orientationAngle", orientation);
  return { deviceType, orientation };
}

// Infer device class from the supplied user agent string.
function detectDeviceType(userAgent: string): DeviceType {
  const isMobile =
    /iPhone|iPod|Android(?=.*Mobile)|Windows Phone|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent
    );
  const isTablet =
    /iPad|Android(?!.*Mobile)|Windows(?=.*Touch)|Kindle|Silk|Tablet/i.test(userAgent);

  if (isTablet) {
    return "tablet";
  }

  if (isMobile) {
    return "smartphone";
  }

  return "pc";
}

// Capture orientation metadata once during startup for downstream helpers.
(() => {
  try {
    const deviceType = detectDeviceType(navigator.userAgent);
    getDeviceOrientation(deviceType);
  } catch (error) {
    if (error instanceof Error) {
      console.error(error.message);
    } else {
      console.error(error);
    }
  }
})();

// Report whether the viewport is currently portrait.
export const isPortrait = (): boolean => {
  return window.matchMedia("(orientation: portrait)").matches;
};

// Kick off device-specific sidebar logic once the DOM is ready.
document.addEventListener("DOMContentLoaded", () => {
  if (document.body) {
    document.body.classList.add("loaded");
  }

  const deviceType = detectDeviceType(navigator.userAgent);

  if (deviceType === "tablet") {
    tabletSidebar();
  } else if (deviceType === "smartphone") {
    const initialized = mobileSidebar();

    if (!initialized) {
      setupDesktopSidebarFallback({
        lockOnNarrow: false,
        defaultState: STATE_CLOSE,
        overlay: true,
        storageKey: "toggleSlider:mobile"
      });
    }
  } else {
    setupDesktopSidebarFallback();
  }

  const scrollLinks = document.querySelectorAll<HTMLAnchorElement>(".scroll-link");

  scrollLinks.forEach((link) => {
    link.addEventListener("click", (event) => {
      event.preventDefault();
      const targetId = link.getAttribute("data-target");
      if (!targetId) {
        return;
      }

      const targetElement = document.getElementById(targetId);

      if (!targetElement) {
        return;
      }

      if (deviceType === "smartphone" && isPortrait()) {
        window.setTimeout(() => {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest"
          });
        }, 1000);
      } else {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest"
        });
      }
    });
  });

  const hljs = (window as Window & { hljs?: { highlightAll?: () => void } }).hljs;
  if (hljs?.highlightAll) {
    hljs.highlightAll();
  }

  const SwiperCtor = (window as Window & { Swiper?: new (...args: any[]) => unknown }).Swiper;
  if (SwiperCtor && document.querySelector(".swiper")) {
    new SwiperCtor(".swiper", {
      direction: "horizontal",
      loop: false,
      scrollbar: {
        el: ".swiper-scrollbar",
      },
      slidesPerView: "auto",
      spaceBetween: 15,
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
      },
    });
  }

  const jq = (window as Window & { jQuery?: any; $?: any }).jQuery || (window as Window & { $?: any }).$;
  if (jq) {
    jq(() => {
      const $topButtons = jq(".topBtn");
      if (!$topButtons.length) {
        return;
      }

      const scrollThreshold = 500;
      const $window = jq(window);
      const $main = jq(".main");

      const getScrollPosition = () => {
        const windowScroll = $window.scrollTop();
        const mainScroll = $main.length ? $main.scrollTop() : 0;
        return Math.max(windowScroll || 0, mainScroll || 0);
      };

      const toggleTopButton = () => {
        if (getScrollPosition() > scrollThreshold) {
          $topButtons.stop(true, true).fadeIn();
        } else {
          $topButtons.stop(true, true).fadeOut();
        }
      };

      const scrollToTop = () => {
        if ($main.length && $main[0].scrollHeight > $main.innerHeight()) {
          $main.stop().animate({ scrollTop: 0 }, "slow");
        }

        jq("html, body").stop().animate({ scrollTop: 0 }, "slow");
      };

      $window.on("scroll", toggleTopButton);
      if ($main.length) {
        $main.on("scroll", toggleTopButton);
      }

      $topButtons.on("click", (event: Event) => {
        event.preventDefault();
        scrollToTop();
      });

      toggleTopButton();
    });
  }

  const codeBlocks = document.querySelectorAll("pre");
  if (codeBlocks.length) {
    codeBlocks.forEach((block) => {
      const button = document.createElement("button");
      button.className = "copy-code-btn";
      button.textContent = "Copy code";

      button.addEventListener("click", () => {
        const code = block.querySelector("code");
        if (!code) {
          return;
        }
        const range = document.createRange();
        range.selectNode(code);

        const selection = window.getSelection();
        if (!selection) {
          return;
        }
        selection.removeAllRanges();
        selection.addRange(range);

        document.execCommand("copy");
        selection.removeAllRanges();

        button.textContent = "Copied!";
        window.setTimeout(() => {
          button.textContent = "Copy code";
        }, 2000);
      });

      block.appendChild(button);
    });
  }
});
